import { t } from "@/lib/i18n";
import {
  account_copy,
  type AccountCopy,
  get_audio,
  get_temp_audio,
  invalidate_audio_cache,
  type AudioRequest,
  type VoiceActorInfo,
} from "../../../yap-frontend-rs/pkg";

import {
  interruptPlayback,
  registerPlayback,
  type PlaybackOptions,
} from "./pure";

export type { VoiceActorInfo };
export * from "./pure";

export async function playAudio(
  audioRequest: AudioRequest,
  accessToken: string | undefined,
  needsAuth: (copy: AccountCopy) => void,
  {
    temporary = false,
    onAudioElement,
    signal,
    onVoiceActor,
  }: PlaybackOptions = {},
): Promise<void> {
  const abortError = () => new DOMException("Aborted", "AbortError");
  if (signal?.aborted) throw abortError();
  interruptPlayback();

  return new Promise((resolve, reject) => {
    let audio: HTMLAudioElement | undefined;
    let audioUrl: string | undefined;
    let settled = false;

    const finish = (error?: unknown) => {
      if (settled) return;
      settled = true;
      unregister();
      signal?.removeEventListener("abort", interrupt);
      if (audio) {
        // Clearing src can fire an error; detach handlers before teardown.
        audio.onended = null;
        audio.onerror = null;
        try {
          audio.pause();
          audio.src = "";
        } catch {
          // The element may already be detached.
        }
      }
      if (audioUrl) URL.revokeObjectURL(audioUrl);
      if (error === undefined) resolve();
      else reject(error);
    };

    const interrupt = () => finish(abortError());
    const unregister = registerPlayback(interrupt);
    signal?.addEventListener("abort", interrupt, { once: true });

    const playbackFailed = (error: unknown) => {
      if (settled) return;
      const failure = error instanceof Error ? error : new Error(String(error));
      finish(failure);
      if (!temporary && failure.name !== "NotAllowedError") {
        void invalidate_audio_cache(audioRequest).catch((error) => {
          console.error("Failed to invalidate audio cache:", error);
        });
      }
    };

    const start = async () => {
      const result = await (temporary ? get_temp_audio : get_audio)(
        audioRequest,
        accessToken,
      );
      if (settled) return;
      if (result.voice_actor) onVoiceActor?.(result.voice_actor);
      if (settled) return;

      audioUrl = URL.createObjectURL(
        new Blob([result.bytes], { type: result.mime_type }),
      );
      audio = new Audio(audioUrl);
      if (onAudioElement) await onAudioElement(audio);
      if (settled) return;

      audio.onended = () => finish();
      audio.onerror = () =>
        playbackFailed(new Error(t("utils-audio-playback-failed")));
      try {
        await audio.play();
      } catch (error) {
        playbackFailed(error);
      }
    };

    void start().catch((error) => {
      if (settled) return;
      finish(error ?? new Error(t("utils-audio-preparation-failed")));
      if (typeof error === "string" && error.includes("400"))
        needsAuth(account_copy());
      console.error("Failed to prepare audio:", error);
    });
  });
}
